import { Box, Stack, TextInput, Textarea, Button, Text, Group } from "@mantine/core";
import { useState } from "react";
import { artist } from "@/content/artist";
import { PageHero } from "@/components/layout/PageHero";
import { PageContainer } from "@/components/layout/PageContainer";

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    // Netlify Forms needs an AJAX POST: a native submit would unmount the
    // form mid-navigation and cancel the request. Encode the fields and POST
    // to the site root, where Netlify intercepts submissions for the
    // build-time-detected "contact" form (see index.html).
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const body = new URLSearchParams(
      new FormData(e.currentTarget) as unknown as Record<string, string>,
    ).toString();
    try {
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      if (!res.ok) throw new Error(`Netlify returned ${res.status}`);
      setSubmitted(true);
    } catch {
      setError("Couldn't send — please email us directly using the addresses above.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <PageHero
        title="Contact"
        subtitle="Booking · press · label · management."
      />
      <PageContainer navOffset={false}>
        <Stack gap="lg" maw={640}>
          <Stack gap={4}>
            {artist.contacts.map((c) => (
              <Text key={c.email} size="sm">
                <Text component="span" tt="uppercase" c="dimmed">
                  {c.role}:
                </Text>{" "}
                {c.name} —{" "}
                <Text component="a" href={`mailto:${c.email}`} c="var(--color-foam)">
                  {c.email}
                </Text>
              </Text>
            ))}
          </Stack>

          {submitted ? (
            <Box
              p="md"
              style={{
                background: "var(--color-deep)",
                borderRadius: 8,
                border: "1px solid var(--accent-mint)",
              }}
            >
              <Text>Thanks — message received.</Text>
            </Box>
          ) : (
            <form
              name="contact"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              onSubmit={handleSubmit}
            >
              {/* Netlify-required hidden fields */}
              <input type="hidden" name="form-name" value="contact" />
              <p hidden>
                <label>
                  Don't fill this out: <input name="bot-field" />
                </label>
              </p>
              <Stack gap="sm">
                <TextInput label="Your name" name="name" required />
                <TextInput label="Email" name="email" type="email" required />
                <Group grow>
                  <TextInput label="Organization" name="org" />
                  <TextInput label="Role" name="role" placeholder="A&R, press, booking…" />
                </Group>
                <Textarea label="Message" name="message" minRows={4} required autosize />
                {error && (
                  <Text size="sm" c="red">
                    {error}
                  </Text>
                )}
                <Button type="submit" color="indigo" loading={submitting}>
                  Send
                </Button>
              </Stack>
            </form>
          )}
        </Stack>
      </PageContainer>
    </>
  );
}
